import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';
import { List, Map } from 'immutable';
import classNames from 'classnames';

import Table, { Column, Row } from '../../helpers/Table';
import config from '../../../config';
import { unwrap } from '../../../utils/ChangeSpy';
import { Spinner } from '../../helpers/Spinner';
import { Card } from '../../helpers/Card';

import style from './style.module.css';

/**
 * Universe panel component.
 *
 * @param props {Object}.
 * @param props.universeColumns {Immutable.List} Target column configuration.
 * @param props.data {Immutable.List} Universe List.
 * @param props.common {Immutable.Map} Common Object.
 * @param props.onSort {Function} Handle event of sorting table.
 */
const UniverseTableComponent = ({
  universeColumns,
  projectData,
  data,
  common,
  onDoubleClick,
  onSort,
  onTableRef,
  onCeoProjectUniverseChange,
}) => {
  const type = config.values.getIn(['eproject', 'types', 'HARVCO_TAGS']);
  const columns = getColumns(
    universeColumns,
    common.get('targetSortField'),
    common.get('targetSortDirection'),
    onSort,
    type,
  );
  const spinner = common.get('universeLoading') ? <Spinner /> : null;

  const label = useMemo(() => {
    const buyerName = projectData.get('buyerName');
    const name = projectData.getIn(['category', 'name']);

    return (
      <div className={style.universe}>
        <span className={style.universeTitle}>SEO Universe</span>
        <label className={style.option}>
          <input name="active" onChange={onCeoProjectUniverseChange} type="radio" value="0" defaultChecked />
          {buyerName} - {name} is the &quot;Active&quot; Buyer - Project
        </label>
        <label className={style.option}>
          <input name="active" onChange={onCeoProjectUniverseChange} type="radio" value="1" />
          ALL Executives that have been assigned to {buyerName} - {name} - Project
        </label>
      </div>
    );
  }, [projectData, onCeoProjectUniverseChange]);

  return (
    <Card className={style.host} flush>
      {label}
      <div ref={onTableRef} className={style.tableWrapper}>
        {spinner}
        <Table className={classNames('table table-striped', style.table)}>
          {columns}
          {data.map((target, i) => (
            <Row
              key={target.get('id')}
              data={target.set('i', i + 1)}
              onDoubleClick={() => onDoubleClick({ row: target, type })}
            />
          ))}
        </Table>
      </div>
    </Card>
  );
};

UniverseTableComponent.propTypes = {
  common: PropTypes.instanceOf(Map).isRequired,
  data: PropTypes.instanceOf(List).isRequired,
  onSort: PropTypes.func.isRequired,
  universeColumns: PropTypes.instanceOf(List).isRequired,
};

export const UniverseTable = memo(UniverseTableComponent);

/**
 * Generate table columns.
 *
 * @param universeColumns {Immutable.List} Target column configuration.
 * @param field {Immutable.List} Target List.
 * @param direction {String} Direction of sorting.
 * @param onClick {Function} Handle event of clicking on column head.
 * @param type {String} Type of table.
 */
function getColumns(universeColumns, field, direction, onClick, type) {
  return universeColumns.map((column, index) => {
    const columnField = column.get('field');
    const sortable = column.get('sortable');

    if (columnField === 'legacyPeExperience') {
      return drawLegacyPeExperienceColumn(
        index,
        column.get('headerName'),
        columnField,
        onClick,
        columnField === field ? direction : null,
        type,
      );
    }

    if (columnField === 'currentEprojectCategory') {
      return drawEProjectCategoryColumn(
        index,
        column.get('headerName'),
        columnField,
        onClick,
        columnField === field ? direction : null,
        type,
        sortable,
      );
    }

    return drawColumn(
      index,
      column.get('headerName'),
      columnField,
      onClick,
      columnField === field ? direction : null,
      type,
      sortable,
    );
  });
}

/**
 * Draw column head.
 *
 * @param key {number} Column key.
 * @param headerName {String} Column title.
 * @param field {String} Sorting field name.
 * @param onClick {Function} Handle event of clicking on column head.
 * @param direction {String} Direction of sorting.
 * @param type {String} Type of table.
 * @param sortable {Boolean} Is sortable.
 */
function drawColumn(key, headerName, field, onClick, direction, type, sortable) {
  if (!sortable) {
    return <Column key={key} field={field} title={<span className="text-regular">{headerName}</span>} />;
  }

  return (
    <Column
      key={key}
      field={field}
      title={
        <a
          className="text-regular"
          onClick={event =>
            onClick(event, {
              field,
              direction: direction === 'up' ? 'down' : 'up',
              type,
            })
          }
        >
          {headerName} {direction ? <i aria-hidden="true" className={`fa fa-caret-${direction}`} /> : null}
        </a>
      }
    />
  );
}

/**
 * Generate True|False value column.
 *
 * @param key {number} Column key.
 * @param headerName {String} Column title.
 * @param field {String} Sorting field name.
 * @param onClick {Function} Handle event of clicking on column head.
 * @param direction {String} Direction of sorting.
 * @param type {String} Type of table.
 */
function drawLegacyPeExperienceColumn(key, headerName, field, onClick, direction, type) {
  return (
    <Column
      key={key}
      className={style.priorityColumn}
      field={field}
      title={
        <a
          className="text-regular"
          onClick={event =>
            onClick(event, {
              field,
              direction: direction === 'up' ? 'down' : 'up',
              type,
            })
          }
        >
          {headerName} {direction ? <i aria-hidden="true" className={`fa fa-caret-${direction}`} /> : null}
        </a>
      }
      valueWrapper={value => (unwrap(value) ? 'Yes' : 'No')}
    />
  );
}

/**
 * Draw ceo project category head.
 *
 * @param key {number} Column key.
 * @param headerName {String} Column title.
 * @param field {String} Sorting field name.
 * @param onClick {Function} Handle event of clicking on column head.
 * @param direction {String} Direction of sorting.
 * @param type {String} Type of table.
 * @param sortable {Boolean} Is sortable.
 */
function drawEProjectCategoryColumn(key, headerName, field, onClick, direction, type, sortable) {
  if (!sortable) {
    return <Column key={key} field={field} title={<span className="text-regular">{headerName}</span>} />;
  }

  return (
    <Column
      key={key}
      field={field}
      title={
        <a
          className="text-regular"
          onClick={event =>
            onClick(event, {
              field,
              direction: direction === 'up' ? 'down' : 'up',
              type,
            })
          }
        >
          {headerName} {direction ? <i aria-hidden="true" className={`fa fa-caret-${direction}`} /> : null}
        </a>
      }
      valueWrapper={(value, i) => (
        <a href={`/project/${i.get('currentEprojectId')}`} rel="noreferrer" target="_blank">
          {value}
        </a>
      )}
    />
  );
}
